import z from "zod"
import {
  ProjectTargetGroup,
  ProjectType,
  nullableInput,
  requiredNumberField,
} from "toptaal-shared"

export const studentTypes = z.enum([
  "wi2013_elip",
  "wi2013_civic",
  "wi2021_family",
  "wi2021_status",
  "private",
  "web",
  "other",
])

export type StudentTypes = z.infer<typeof studentTypes>

export const studentTypeMap = {
  wi2013_elip: "ELIP WI2013",
  wi2013_civic: "Inburgeraar WI2013",
  wi2021_family: "Gezinsmigrant WI2021",
  wi2021_status: "Statushouder WI2021",
  private: "Particulier (niet inburgeringsplichtig)",
  web: "WEB",
  other: "Overig",
}

export const studententrySources = z.enum(
  [
    "website",
    "language_house",
    "municipality",
    "employer",
    "organization",
    "personal_network",
    "social_media",
  ],
  {
    message: "Dit veld is verplicht",
  },
)

export const studententrySourceMap = {
  website: "Website",
  language_house: "Taalhuis",
  municipality: "Gemeente",
  employer: "Werkgever",
  organization: "Organisatie",
  personal_network: "Eigen netwerk",
  social_media: "Social media",
}

export type StudentEntrySource = z.infer<typeof studententrySources>

export const studentTypeDropdownOptions = studentTypes.options.map((type) => ({
  label: studentTypeMap[type],
  value: type,
}))

const inburgeringStudentTypes: StudentTypes[] = [
  "wi2013_elip",
  "wi2013_civic",
  "wi2021_family",
  "wi2021_status",
  "private",
]

const webStudentTypes: StudentTypes[] = ["web"]

const taalInBedrijfStudentTypes: StudentTypes[] = ["other"]

export const getStudentTypesForProjectType = (type?: ProjectType | null) => {
  if (type === "inburgering") {
    return inburgeringStudentTypes
  }
  if (type === "web") {
    return webStudentTypes
  }
  if (type === "taal_in_bedrijf") {
    return taalInBedrijfStudentTypes
  }

  return studentTypes.options
}

export const getStudentTypeOptionsForProjectType = (
  type?: ProjectType | null,
) =>
  getStudentTypesForProjectType(type).map((studentType) => ({
    label: studentTypeMap[studentType],
    value: studentType,
  }))

const targetGroupToStudentTypeMap: Record<ProjectTargetGroup, StudentTypes> = {
  wi2013_elip: "wi2013_elip",
  wi2013_civic: "wi2013_civic",
  wi2021_family: "wi2021_family",
  wi2021_status: "wi2021_status",
  web_non_formeel: "web",
  web_nt1: "web",
  web_nt2: "web",
  web_nt1_nt2: "web",
}

export const getStudentTypesForProject = (
  type?: ProjectType | null,
  targetGroups?: ProjectTargetGroup[] | null,
): StudentTypes[] => {
  if (targetGroups && targetGroups.length > 0) {
    return [
      ...new Set(targetGroups.map((tg) => targetGroupToStudentTypeMap[tg])),
    ]
  }

  return getStudentTypesForProjectType(type)
}

export const getStudentTypeOptionsForProject = (
  type?: ProjectType | null,
  targetGroups?: ProjectTargetGroup[] | null,
) =>
  getStudentTypesForProject(type, targetGroups).map((studentType) => ({
    label: studentTypeMap[studentType],
    value: studentType,
  }))

export const studententrySourceDropdownOptions =
  studententrySources.options.map((source) => ({
    label: studententrySourceMap[source],
    value: source,
  }))

const baseProjectStepSchema = z.object({
  projectId: nullableInput(requiredNumberField),
  organizationId: nullableInput(requiredNumberField),
  studentType: studentTypes.optional().nullable(),
  externalStudentFileNumber: z.string().optional().nullable(),
  billingOrganizationId: nullableInput(requiredNumberField),
  reportingOrganizationId: nullableInput(requiredNumberField),
  isStudentFileNumberRequired: z.boolean().optional(),
})

export const projectStepSchema = baseProjectStepSchema
  .extend({
    isEntrySourceRequired: z.boolean().nullable().optional(),
    entrySource: studententrySources.optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.isEntrySourceRequired === true && !data.entrySource) {
      ctx.addIssue({
        path: ["entrySource"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht",
      })
    }
    if (data.isStudentFileNumberRequired && !data.externalStudentFileNumber) {
      ctx.addIssue({
        path: ["externalStudentFileNumber"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht",
      })
    }
  })

export const intakeProjectStepSchema = projectStepSchema.superRefine(
  (data, ctx) => {
    if (!data.studentType) {
      ctx.addIssue({
        path: ["studentType"],
        code: z.ZodIssueCode.custom,
        message: "Dit veld is verplicht",
      })
    }
  },
)

export type ProjectSchemaInput = z.input<typeof projectStepSchema>
export type ProjectSchemaOutput = z.infer<typeof projectStepSchema>
